/**
 * Fold guides on the printed sheet, as on the user's own print that folded
 * well: every fold line on both sides as a faint solid line with the number
 * of the step that folds it, and two dashed lines to line folds up with - the
 * centre line and the line across the sheet (src/template/print-guides.js).
 *
 * The lines are printed UNDER the pictures: the sheet draws them first and the
 * pictures on top, so a picture simply covers them. They used to be drawn on
 * top and hidden with SVG masks, which the PDF carries as soft masks - and
 * many printers and PDF viewers ignore those, printing every line across the
 * photographs (seen by the user, 2026-09-27). Where a line must not show at all
 * it is now cut out of the line itself, so the file needs no transparency.
 *
 * Nothing is drawn where it would show on the finished card: lines stop on
 * paper that faces outwards when the card is closed - except that a fold line
 * is kept along the edges of that paper, because there it lies on the folded
 * edge of the card - and they stop at notes that have no picture under them.
 * Step numbers sit on hidden paper only.
 */
import { useMemo } from 'react';
import type { PrintGuides, Pt } from '@foldmycard/scene';

type FaceGuides = PrintGuides['faces'][number];

const pts = (p: Pt[]) => p.map((q) => `${q[0]},${q[1]}`).join(' ');

function inside(poly: Pt[], [x, y]: Pt): boolean {
  let hit = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) hit = !hit;
  }
  return hit;
}

const MARKER_R = 2.6;          // mm
const EDGE = 9;                // mm kept clear of the paper edge - printers cannot print the last few
const CLEAR = MARKER_R + 1.2;  // mm kept clear of anything that shows

/**
 * Where each fold's step number goes: along the fold line, as near to one of
 * its ends as possible, but inside the printable area, off every blocked area
 * (outside paper, artwork), and not on top of another number.
 */
export function markerSpots(guides: FaceGuides, W: number, H: number, blocked: Pt[][]): { label: string; at: Pt }[] {
  const out: { label: string; at: Pt }[] = [];
  const ring = Array.from({ length: 12 }, (_, i) => [Math.cos((i * Math.PI) / 6), Math.sin((i * Math.PI) / 6)]);
  const free = (p: Pt) =>
    p[0] >= EDGE && p[1] >= EDGE && p[0] <= W - EDGE && p[1] <= H - EDGE &&
    [[0, 0], ...ring].every(([dx, dy]) => !blocked.some((b) => inside(b, [p[0] + dx * CLEAR, p[1] + dy * CLEAR]))) &&
    out.every((m) => Math.hypot(m.at[0] - p[0], m.at[1] - p[1]) > 3 * MARKER_R);
  for (const fold of guides.folds) {
    if (!fold.label) continue;          // a dashed guide: never folded, so no number
    const segs = [...fold.segments].sort((a, b) => Math.hypot(b[1][0] - b[0][0], b[1][1] - b[0][1]) - Math.hypot(a[1][0] - a[0][0], a[1][1] - a[0][1]));
    let found: Pt | null = null;
    for (const [a, b] of segs) {
      const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
      const u: Pt = [(b[0] - a[0]) / L, (b[1] - a[1]) / L];
      for (let d = 4; d < L / 2 && !found; d += 1) {
        for (const p of [[a[0] + u[0] * d, a[1] + u[1] * d], [b[0] - u[0] * d, b[1] - u[1] * d]] as Pt[]) {
          if (free(p)) { found = p; break; }
        }
      }
      if (found) break;
    }
    if (found) out.push({ label: fold.label, at: found });
  }
  return out;
}

/** mm a fold line may run inside outward-facing paper: along its edges, where the card is folded. */
const FOLD_EDGE = 2;
/** mm a dashed guide may: only exactly on an edge, where another layer's edge covers it. */
const GUIDE_EDGE = 0.3;
/** mm kept between a line and a note printed without a picture. */
const NOTE_CLEAR = 1.5;
/** mm between the points a line is tested at. */
const STEP = 0.25;

function distToOutline(poly: Pt[], [x, y]: Pt): number {
  let best = Infinity;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [ax, ay] = poly[j], [bx, by] = poly[i];
    const dx = bx - ax, dy = by - ay;
    const L2 = dx * dx + dy * dy || 1;
    const t = Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / L2));
    best = Math.min(best, Math.hypot(x - (ax + t * dx), y - (ay + t * dy)));
  }
  return best;
}

interface Area { poly: Pt[]; box: [number, number, number, number] }
const area = (poly: Pt[], pad: number): Area => {
  const xs = poly.map((p) => p[0]), ys = poly.map((p) => p[1]);
  return { poly, box: [Math.min(...xs) - pad, Math.min(...ys) - pad, Math.max(...xs) + pad, Math.max(...ys) + pad] };
};
const inBox = ({ box }: Area, [x, y]: Pt) => x >= box[0] && y >= box[1] && x <= box[2] && y <= box[3];

/**
 * The parts of each printed line that may be drawn, as plain segments: tested
 * every quarter millimetre against the outward-facing paper and the notes.
 */
export function visibleLines(guides: FaceGuides, notes: Pt[][]) {
  const outside = guides.outside.map((p) => area(p, FOLD_EDGE));
  const noteAreas = notes.map((p) => area(p, NOTE_CLEAR));
  return guides.folds.map((f) => {
    const kind = f.kind ?? 'fold';
    const edge = kind === 'fold' ? FOLD_EDGE : GUIDE_EDGE;
    const allowed = (p: Pt) =>
      outside.every((o) => !inBox(o, p) || !inside(o.poly, p) || distToOutline(o.poly, p) <= edge) &&
      noteAreas.every((n) => !inBox(n, p) || (!inside(n.poly, p) && distToOutline(n.poly, p) > NOTE_CLEAR));
    const runs: [Pt, Pt][] = [];
    for (const [a, b] of f.segments) {
      const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
      const n = Math.max(1, Math.ceil(L / STEP));
      const at = (i: number): Pt => [a[0] + ((b[0] - a[0]) * i) / n, a[1] + ((b[1] - a[1]) * i) / n];
      let start = -1;
      for (let i = 0; i <= n; i++) {
        const ok = allowed(at(i));
        if (ok && start < 0) start = i;
        if ((!ok || i === n) && start >= 0) {
          const end = ok ? i : i - 1;
          if (end > start) runs.push([at(start), at(end)]);
          start = -1;
        }
      }
    }
    return { line: f.line, kind, runs };
  });
}

/**
 * The lines, for drawing BEFORE the pictures. `notes`: outlines of zones that
 * carry a note and no picture - nothing else covers the line there.
 */
export function FoldLines({ guides, notes }: { guides: FaceGuides; notes: Pt[][] }) {
  const lines = useMemo(() => visibleLines(guides, notes), [guides, notes]);
  return (
    <g className="fold-lines" pointerEvents="none">
      {lines.flatMap((l) => l.runs.map((r, i) => (
        <line key={`${l.line}-${i}`} x1={r[0][0]} y1={r[0][1]} x2={r[1][0]} y2={r[1][1]}
          stroke="#8a939c" strokeWidth={0.3} strokeDasharray={l.kind === 'guide' ? '2.4 1.6' : undefined} />
      )))}
    </g>
  );
}

/** The step numbers, for drawing AFTER the pictures; they sit on hidden paper only. */
export function FoldMarkers({ guides, W, H, artwork }: { guides: FaceGuides; W: number; H: number; artwork: Pt[][] }) {
  const spots = useMemo(() => markerSpots(guides, W, H, [...guides.outside, ...artwork]), [guides, W, H, artwork]);
  return (
    <g className="fold-markers" pointerEvents="none">
      {spots.map((m) => (
        <g key={m.label + m.at.join()}>
          <circle cx={m.at[0]} cy={m.at[1]} r={MARKER_R} fill="#fff" stroke="#6b7580" strokeWidth={0.25} />
          <text x={m.at[0]} y={m.at[1]} textAnchor="middle" dominantBaseline="central" fill="#4a535c"
            fontFamily="'Noto Sans', sans-serif" fontWeight={700} fontSize={m.label.length > 1 ? 1.7 : 2.8}>{m.label}</text>
        </g>
      ))}
    </g>
  );
}
